/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { get, remove, isEqual } from 'lodash';
import { parseISO, format, parse } from 'date-fns';
import {
  PeriodOptions,
  StopRepeatingOptions,
  getPeriodDisplayLabel,
  formatStringDate,
  DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
  RecurrenceTypes,
  STOP_REPEATING,
} from './utils';
import { SuggestionFactory, SuggestionType } from './suggestionsFactory';
import { parseRule, RULES_PERIODS } from './rules';

/**
 * Returns a recurrence help message, for the quick suggestions.
 * It takes into account:
 * - On [ordinal date / xday of the week / last day / last xday of the week / last weekday]
 * for the intervals [weekly, monthly, quarterly, semiannually, annually]
 * - When it ends: never / after specific date / on X number of occurrences.
 * @param formData FormData
 */
export function getQuickSuggestionsMessage(formData): string {
  let repeat = get(formData, 'repeatOption.label', PeriodOptions.DoesNotRepeat);
  const repeatMessage = repeat.substring(3, repeat.length);
  let stopRepeatingText = stopRepeating(formData);
  stopRepeatingText = stopRepeatingText ? `${stopRepeatingText}` : '.';
  return `Due <strong>${repeatMessage}</strong>${stopRepeatingText}`;
}

/**
 * Returns a recurrence help message, for the custom repeat.
 * It takes into account:
 * - Due every X number [week, month, quarter, year] (period)
 * - On [ordinal date / xday of the week / last day / last xday of the week / last weekday]
 * - When it ends: never / after specific date / on X number of occurrences
 * @param formData FormData
 */
export function getCustomRepeatMessage(formData): string {
  // 1, 2, .... week[s] / month[s] / quarter[s], etc
  let period = get(formData, 'period.value', '');
  period = getPeriodDisplayLabel(period);
  let repeatText = period.toLowerCase();
  const every = +get(formData, 'repeatInterval');
  let everyText = '';
  if (every > 1) {
    repeatText += 's';
    everyText = `${every} `;
  }

  // custom repeat quick suggestions: on
  let repeatOn = get(formData, 'repeatOn.value', '');
  if (repeatOn) {
    repeatOn = ` on ${repeatOn}`;
  }

  let stopRepeatingText = stopRepeating(formData);
  stopRepeatingText = stopRepeatingText ? `${stopRepeatingText}` : '.';
  return `Due every <strong>${everyText}${repeatText}${repeatOn}</strong>${stopRepeatingText}`;
}

/* PATTERNS
  * Weekly:
  * - on [whatever day of the week my Due Date is], for example:
  * e.g. Weekly on Fridays
  
  * Monthly, Quarterly, Semiannually, Annually:
  * - on [whatever calendar day my Due Date is], for example:
  * e.g. Monthly on the 21st
  * e.g. Monthly on the last day of the month (only when the selected calendar day for the Due Date happens to be last day of that month)
  * - on [relative day of the week], for example:
  * e.g. Monthly on the 2nd Tuesday [of the month {1} / of _MONTH_ {2}]
  * e.g. Monthly on the last Friday [of the month {1} / of _MONTH_ {2}] (note: if the user selects the 5th occurrence of a weekday in a given month, we treat this as the “last” occurrence of that weekday rather than the “fifth” occurrence of that weekday, since in most months that day will occur only 4 times)
  * e.g. Monthly on the last weekday [of the month {1} / of _MONTH_ {2}] (only when the selected Due Date happens to be the last weekday of the month)
  * {2}: if is annually, {1} otherwise
  **/
export function buildQuickSuggestions(isoEndDate: string) {
  const suggestions = [
    {
      label: PeriodOptions.DoesNotRepeat,
      value: PeriodOptions.DoesNotRepeat,
    },
  ];

  if (isoEndDate) {
    const endDate: Date = parseISO(isoEndDate);
    const options = createCommonOptions(endDate);
    Object.keys(options).forEach(p => {
      suggestions.push(
        ...options[p].map(suggestion => {
          const sug = {
            label: `due ${p} on ${suggestion.label}`,
            value: `due ${p} on ${suggestion.label}`,
          };
          if (suggestion.rule) sug.rule = suggestion.rule;
          return sug;
        })
      );
    });
  }

  const custom = {
    label: PeriodOptions.Custom,
    value: PeriodOptions.Custom,
  };

  suggestions.push(custom);
  return suggestions;
}

export function buildCustomRepeatQuickSuggestions(isoEndDate: string, filterPeriods: Array<string> = []) {
  const endDate: Date = parseISO(isoEndDate);
  let suggestions = [];
  const options = createCommonOptions(endDate, filterPeriods);
  Object.keys(options).forEach(p => {
    suggestions.push(...options[p]);
  });
  return suggestions;
}

export function buildForm({ startDate, dueOn, recurrenceType, rule }) {
  const today = new Date();
  let form = {
    startDate: null,
    endDate: null,
    repeatOption: null,
    stopRepeating: STOP_REPEATING[0],
    stopOn: null,
    repeatOn: null,
    repeatTimes: 1,
    repeatInterval: 1,
    period: null,
  };

  form.startDate = startDate
    ? format(parse(startDate, DISPLAY_DATE_FORMAT, new Date()), DATE_FORMAT)
    : format(today, DATE_FORMAT);

  form.endDate = dueOn ? format(parse(dueOn, DISPLAY_DATE_FORMAT, new Date()), DATE_FORMAT) : null;

  if (rule) {
    const parsedRule = parseRule(rule);
    const options = parsedRule.origOptions;

    if (options.until) {
      form.stopRepeating = STOP_REPEATING[1];
      form.stopOn = parsedRule.options.untilString;
      delete options.until;
    } else if (options.count) {
      form.stopRepeating = STOP_REPEATING[2];
      form.repeatTimes = options.count;
      delete options.count;
    }

    if (recurrenceType === RecurrenceTypes.Standard) {
      const quickSuggestions = buildQuickSuggestions(form.endDate);
      const suggestion = quickSuggestions.find(sug => isEqual(sug.rule, options));
      if (suggestion) {
        form.repeatOption = suggestion;
      }
    } else if (recurrenceType === RecurrenceTypes.Custom) {
      form.repeatOption = {
        label: PeriodOptions.Custom,
        value: PeriodOptions.Custom,
      };

      form.period = RULES_PERIODS[options.freq];

      if (options.interval) {
        form.repeatInterval = options.interval;
        delete options.interval;
      }

      const customQuickSuggestions = buildCustomRepeatQuickSuggestions(form.endDate, [form.period.value]);
      const suggestion = customQuickSuggestions.find(sug => isEqual(sug.rule, options));
      if (suggestion) {
        form.repeatOn = suggestion;
      }
    }
  }

  return form;
}

function createCommonOptions(
  endDate: Date,
  periods: Array<string> = [
    PeriodOptions.Weekly,
    PeriodOptions.Monthly,
    PeriodOptions.Quarterly,
    PeriodOptions.Semiannually,
    PeriodOptions.Annually,
  ]
) {
  const options = {};
  const suggestionFactory = new SuggestionFactory(endDate);

  if (periods.includes(PeriodOptions.Weekly)) {
    const daySuggestion = suggestionFactory.getSuggestion(PeriodOptions.Weekly, SuggestionType.Day);
    if (daySuggestion) options[PeriodOptions.Weekly.toLowerCase()] = [daySuggestion];
    remove(periods, item => item === PeriodOptions.Weekly);
  }

  periods.forEach(period => {
    const suggestions = [];
    const dateSuggestion = suggestionFactory.getSuggestion(period, SuggestionType.Date);
    if (dateSuggestion) suggestions.push(dateSuggestion);

    const lastDateSuggestion = suggestionFactory.getSuggestion(period, SuggestionType.LastDay);
    if (lastDateSuggestion) suggestions.push(lastDateSuggestion);

    const nWeekDaySuggestion = suggestionFactory.getSuggestion(period, SuggestionType.NWeekDay);
    if (nWeekDaySuggestion) suggestions.push(nWeekDaySuggestion);

    const weekDaySuggestion = suggestionFactory.getSuggestion(period, SuggestionType.LastWorkDay);
    if (weekDaySuggestion) suggestions.push(weekDaySuggestion);

    options[period.toLowerCase()] = suggestions;
  });

  return options;
}

function stopRepeating(formData): string {
  if (formData.stopRepeating) {
    if (formData.stopRepeating.value === StopRepeatingOptions.Never) {
      return ', until canceled.';
    }

    if (formData.stopRepeating.value === StopRepeatingOptions.On && formData.stopOn) {
      return ` through <strong>${formatStringDate(formData.stopOn, 'MMMM do yyyy')}</strong>.`;
    }

    if (formData.stopRepeating.value === StopRepeatingOptions.After && formData.repeatTimes > 0) {
      return `, for <strong>${formData.repeatTimes} occurrence${formData.repeatTimes > 1 ? 's' : ''}</strong>.`;
    }
  }

  return '';
}
